import i18next from 'i18next';
import PropTypes from 'prop-types';
import React from 'react';

import { Tooltip } from '@commons/utils/styledLibraryComponents';
import DisplayNumber, { formatNumber, PRICE_NUMBER_DECIMALS } from '@commons/DisplayNumber';
import EmptyState from '@commons/EmptyState';
import Text, { ENUM_COLORS, ENUM_FONTS } from '@commons/Text';

import { Cell, ColumnsRow, ProductRow, TableContainer } from '../../../orderTableStyledComponents';
import { EXTERNAL_ORDER_VAT_RATE_DECIMALS } from '../../../../constants';
import OrderCategoryRow from '../../../OrderCategoryRow';
import OrderProductNameCell from '../../../OrderProductNameCell';

import { ColumnLabelWithTooltip, TooltipIcon } from './styledComponents';

const COLUMN_FLEX = {
  PRODUCT: 4,
  PRICE: 2,
  QUANTITY: 2,
  TOTAL_WITHOUT_TAXES: 2,
  VAT_RATE: 2,
  TOTAL_WITH_TAXES: 2,
};

/** Trailing zeros of a fixed-decimals number, along with the separator they follow */
const TRAILING_ZEROS = /\.?0+$/;

/**
 * A VAT rate is usually whole (10 %) but can carry decimals (5,5 %), so it is formatted with enough
 * precision for them and then trimmed of the zeros that precision adds to a rounder rate.
 */
const formatVatRate = (vatRate) => {
  if (vatRate == null) {
    return '-';
  }

  return `${formatNumber(vatRate, EXTERNAL_ORDER_VAT_RATE_DECIMALS).replace(TRAILING_ZEROS, '')}%`;
};

/**
 * Ordered lines of an order that left the draft status, grouped by category like the draft table.
 * Everything is read-only here: the quantities are frozen until the preparation starts, and so are
 * the unit price and the VAT rate snapshotted on the line — a catalog price changed since is not
 * reflected.
 */
const OrderProductTable = (props) => {
  const { categoryGroups, collapsedCategories, hasSearch, onToggleCategory } = props;

  if (!categoryGroups.length) {
    return (
      <EmptyState
        label={i18next.t(
          hasSearch
            ? 'EXTERNAL_ORDERS.FORM.NO_PRODUCT_SEARCH_RESULT'
            : 'EXTERNAL_ORDERS.FORM.NO_ORDER_PRODUCT',
        )}
      />
    );
  }

  const renderColumnLabel = (label) => (
    <Text color={ENUM_COLORS.DARKER} font={ENUM_FONTS.TEXT_BIG_HEIGHT_16}>
      {label}
    </Text>
  );

  const renderVatRateTooltipIcon = () => (
    <TooltipIcon src={'/images/inpulse/info-dmgrey-small.svg'} />
  );

  const renderProductRow = (productLine) => {
    const {
      id,
      name,
      sku,
      img,
      priceWithTaxes,
      quantity,
      vatRate,
      totalWithoutTaxes,
      totalWithTaxes,
    } = productLine;

    return (
      <ProductRow key={id}>
        <Cell flex={COLUMN_FLEX.PRODUCT}>
          <OrderProductNameCell img={img} name={name} sku={sku} />
        </Cell>
        <Cell flex={COLUMN_FLEX.PRICE}>
          <DisplayNumber
            customNumberDecimal={PRICE_NUMBER_DECIMALS}
            font={ENUM_FONTS.TEXT_BIG_HEIGHT_16}
            number={priceWithTaxes}
            displayCurrencyCode
          />
        </Cell>
        <Cell flex={COLUMN_FLEX.QUANTITY}>
          <Text color={ENUM_COLORS.DARKEST} font={ENUM_FONTS.TEXT_BIG_HEIGHT_16}>
            {formatNumber(quantity)}
          </Text>
        </Cell>
        <Cell flex={COLUMN_FLEX.TOTAL_WITHOUT_TAXES}>
          <DisplayNumber
            font={ENUM_FONTS.TEXT_BIG_HEIGHT_16}
            number={totalWithoutTaxes}
            displayCurrencyCode
          />
        </Cell>
        <Cell flex={COLUMN_FLEX.VAT_RATE}>
          <Text color={ENUM_COLORS.DARKEST} font={ENUM_FONTS.TEXT_BIG_HEIGHT_16}>
            {formatVatRate(vatRate)}
          </Text>
        </Cell>
        <Cell flex={COLUMN_FLEX.TOTAL_WITH_TAXES}>
          <DisplayNumber
            font={ENUM_FONTS.TEXT_BIG_HEIGHT_16}
            number={totalWithTaxes}
            displayCurrencyCode
          />
        </Cell>
      </ProductRow>
    );
  };

  return (
    <TableContainer>
      <ColumnsRow>
        <Cell flex={COLUMN_FLEX.PRODUCT}>{renderColumnLabel(i18next.t('GENERAL.PRODUCT'))}</Cell>
        <Cell flex={COLUMN_FLEX.PRICE}>
          {renderColumnLabel(i18next.t('EXTERNAL_ORDERS.FORM.COLUMN_UNIT_PRICE'))}
        </Cell>
        <Cell flex={COLUMN_FLEX.QUANTITY}>{renderColumnLabel(i18next.t('GENERAL.QUANTITY'))}</Cell>
        <Cell flex={COLUMN_FLEX.TOTAL_WITHOUT_TAXES}>
          {renderColumnLabel(i18next.t('EXTERNAL_ORDERS.FORM.COLUMN_TOTAL_WITHOUT_TAXES'))}
        </Cell>
        <Cell flex={COLUMN_FLEX.VAT_RATE}>
          <ColumnLabelWithTooltip>
            {renderColumnLabel(i18next.t('EXTERNAL_ORDERS.FORM.COLUMN_VAT_RATE'))}
            <Tooltip
              renderTooltipTrigger={renderVatRateTooltipIcon}
              text={i18next.t('EXTERNAL_ORDERS.FORM.VAT_RATE_TOOLTIP')}
              displayBigger
            />
          </ColumnLabelWithTooltip>
        </Cell>
        <Cell flex={COLUMN_FLEX.TOTAL_WITH_TAXES}>
          {renderColumnLabel(i18next.t('EXTERNAL_ORDERS.FORM.COLUMN_TOTAL_WITH_TAXES'))}
        </Cell>
      </ColumnsRow>

      {categoryGroups.map(({ category, products }) => {
        const isCollapsed = collapsedCategories.includes(category);

        return (
          <div key={category}>
            <OrderCategoryRow
              category={category}
              isCollapsed={isCollapsed}
              productCount={products.length}
              onToggle={() => onToggleCategory(category)}
            />
            {!isCollapsed && products.map(renderProductRow)}
          </div>
        );
      })}
    </TableContainer>
  );
};

OrderProductTable.propTypes = {
  categoryGroups: PropTypes.arrayOf(
    PropTypes.shape({
      category: PropTypes.string,
      products: PropTypes.array,
    }),
  ).isRequired,
  collapsedCategories: PropTypes.arrayOf(PropTypes.string).isRequired,
  hasSearch: PropTypes.bool.isRequired,
  onToggleCategory: PropTypes.func.isRequired,
};

export default OrderProductTable;
